import { API } from 'aws-amplify'
import store from '@/store/index.js'

/**
 * Returns true if all 3 hierarchical permissions feature flags are enabled on the tenant.
 */
export function isHierarchicalPermissionsEnabled() {
  const tenant = store.state.userInfo?.tenant
  return !!(tenant?.featureAccessHierarchicalPermissions && tenant?.featureEnabledHierarchicalPermissions && tenant?.permissionHierarchicalPermissions)
}

/**
 * Cache for visible staff sets, keyed by permissionKey.
 * Invalidated on page refresh; manual invalidation via clearVisibleStaffCache().
 */
const cache = {}

/**
 * Fetches the visible staff set for the current user and a given permission.
 *
 * @param {string} permissionKey - e.g. 'permissionAccidents', 'permissionInjuries', 'permissionDocuments'
 * @returns {Promise<{visibleStaffIds: string[]|null, isUnfiltered: boolean}>}
 *   - visibleStaffIds: array of visible staff IDs, or null if no filtering needed
 *   - isUnfiltered: true when scope is 'all' or user is admin
 */
export async function getVisibleStaffSet(permissionKey) {
  const tenantId = store.state.userInfo?.tenant?.group
  if (!tenantId) {
    console.error('[visibleStaffSet] Missing tenant group; failing closed')
    return { visibleStaffIds: [], isUnfiltered: false }
  }

  const cacheKey = `${tenantId}:${permissionKey}`
  if (cache[cacheKey]) return cache[cacheKey]

  try {
    const result = await API.get('rds', `/visible-staff/${permissionKey}`, {
      queryStringParameters: { tenantId }
    })
    const response = result.data ?? result
    const data = {
      visibleStaffIds: response.visibleStaffIds,
      isUnfiltered: response.isUnfiltered
    }
    cache[cacheKey] = data
    return data
  } catch (err) {
    console.error('[visibleStaffSet] Error fetching visible staff:', err)
    // Fail closed — return empty set to match backend behavior
    return { visibleStaffIds: [], isUnfiltered: false }
  }
}

/**
 * Filters an array of records to only include those whose staff ID is in the visible set.
 *
 * @param {Array} records - Array of records to filter
 * @param {string|Function} staffIdAccessor - Field name (e.g. 'accidentStaffId') or function (record => staffId)
 * @param {string} permissionKey - e.g. 'permissionAccidents'
 * @returns {Promise<Array>} Filtered records
 */
export async function filterByVisibleStaff(records, staffIdAccessor, permissionKey) {
  const { visibleStaffIds, isUnfiltered } = await getVisibleStaffSet(permissionKey)
  if (isUnfiltered) return records

  const visibleSet = new Set(visibleStaffIds)
  const getStaffId = typeof staffIdAccessor === 'function'
    ? staffIdAccessor
    : (record) => record[staffIdAccessor]
  return records.filter(record => visibleSet.has(getStaffId(record)))
}

/**
 * Checks if a specific staff ID is in the visible set for a given permission.
 *
 * @param {string} staffId - Staff ID to check
 * @param {string} permissionKey - e.g. 'permissionDocuments'
 * @returns {Promise<boolean>} true if visible or unfiltered
 */
export async function isStaffVisible(staffId, permissionKey) {
  const { visibleStaffIds, isUnfiltered } = await getVisibleStaffSet(permissionKey)
  if (isUnfiltered) return true
  return new Set(visibleStaffIds).has(staffId)
}

/**
 * Clears the visible staff cache. Call when reporting chain or scope settings change.
 */
export function clearVisibleStaffCache() {
  Object.keys(cache).forEach(key => delete cache[key])
}
